import { PresidioDiscoveryUser } from '../models/presidio-discovery.model';

/** Active customer administrators (MVP 1.2.1 / FR18.1 — every account must keep at least one). */
export function isActiveAdministrator(user: PresidioDiscoveryUser | undefined | null): boolean {
  return !!user && user.primaryRole === 'administrator' && user.status === 'Active';
}

function loginKey(login: string | undefined): string {
  return String(login || '').trim().toLowerCase();
}

function accountsFor(user: PresidioDiscoveryUser, homeCustomerNo?: string): string[] {
  if (user.affiliateCustomerNos?.length) {
    return [...user.affiliateCustomerNos];
  }
  return homeCustomerNo ? [homeCustomerNo] : [];
}

/**
 * Whether applying `nextUser` (role demotion, deactivate, and/or removing affiliates)
 * would leave any previously covered ERP account without an Active administrator.
 */
export function wouldLeaveAccountWithoutAdmin(
  users: PresidioDiscoveryUser[],
  nextUser: PresidioDiscoveryUser,
  homeCustomerNo?: string
): { blocked: boolean; customerNos: string[] } {
  const key = loginKey(nextUser.login);
  const current = users.find(u => loginKey(u.login) === key);
  if (!isActiveAdministrator(current)) {
    return { blocked: false, customerNos: [] };
  }

  const before = accountsFor(current as PresidioDiscoveryUser, homeCustomerNo);
  // Demoted / deactivated admins cover no accounts; Active admins cover their next affiliates.
  const after = isActiveAdministrator(nextUser)
    ? new Set(accountsFor(nextUser, homeCustomerNo))
    : new Set<string>();

  if (!before.length) {
    // No affiliate list — treat org-wide: block when coverage is lost and no other active admin exists.
    if (isActiveAdministrator(nextUser)) {
      return { blocked: false, customerNos: [] };
    }
    const otherAdmins = users.filter(u => loginKey(u.login) !== key && isActiveAdministrator(u));
    return { blocked: otherAdmins.length === 0, customerNos: otherAdmins.length === 0 ? ['*'] : [] };
  }

  const lostCoverage = before.filter(customerNo => !after.has(customerNo));
  if (!lostCoverage.length) {
    return { blocked: false, customerNos: [] };
  }

  const stranded = lostCoverage.filter(customerNo => {
    const otherAdmins = users.filter(
      u =>
        loginKey(u.login) !== key &&
        isActiveAdministrator(u) &&
        accountsFor(u, homeCustomerNo).includes(customerNo)
    );
    return otherAdmins.length === 0;
  });

  return { blocked: stranded.length > 0, customerNos: stranded };
}

/** Convenience: true when deactivating this Active administrator would leave an account without an admin. */
export function cannotDeactivateLastAdmin(
  users: PresidioDiscoveryUser[],
  user: PresidioDiscoveryUser,
  homeCustomerNo?: string
): boolean {
  return wouldLeaveAccountWithoutAdmin(users, { ...user, status: 'Disabled' }, homeCustomerNo).blocked;
}

/** Convenience: true when changing this Active administrator to Standard would leave an account without an admin. */
export function cannotDemoteLastAdmin(
  users: PresidioDiscoveryUser[],
  user: PresidioDiscoveryUser,
  homeCustomerNo?: string
): boolean {
  return wouldLeaveAccountWithoutAdmin(
    users,
    { ...user, primaryRole: 'standard' },
    homeCustomerNo
  ).blocked;
}
